// Focus helpers shared by Dialog, DropdownMenu and the shell drawer.

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Elements Tab can reach inside `root`, in DOM order, skipping hidden and inert ones. */
export function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.closest('[inert]')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    // offsetParent is null for display:none (and for fixed elements, which are still visible).
    return el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
  });
}

/**
 * Keeps Tab / Shift+Tab inside `root` (a modal's panel). Native modal dialogs already make the page inert, but Tab
 * can still leave for the browser chrome; wrapping keeps keyboard users in the dialog like a sheet on iOS.
 * Returns true when it moved focus.
 */
export function trapTab(event: KeyboardEvent | ReactKeyboardEvent, root: HTMLElement): boolean {
  if (event.key !== 'Tab') return false;
  const items = getFocusable(root);
  if (items.length === 0) {
    event.preventDefault();
    root.focus();
    return true;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement as HTMLElement | null;
  if (event.shiftKey && (active === first || !root.contains(active))) {
    event.preventDefault();
    last.focus();
    return true;
  }
  if (!event.shiftKey && (active === last || !root.contains(active))) {
    event.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/** Focuses `el` if it is still in the document (restoring focus after a dialog or menu closes). */
export function focusIfConnected(el: Element | null | undefined): void {
  if (el instanceof HTMLElement && el.isConnected) el.focus({ preventScroll: true });
}

/** True while a modal <dialog> is open (global shortcuts should stand down: the page behind it is inert). */
export function isModalOpen(): boolean {
  try {
    return document.querySelector('dialog:modal') !== null;
  } catch {
    // Browsers without the :modal selector: any open dialog counts.
    return document.querySelector('dialog[open]') !== null;
  }
}
